import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ShieldCheck,
  PieChart,
  BarChart3,
  TrendingUp,
  Bell,
  FileText,
  RefreshCcw,
  Users,
  DollarSign,
  AlertTriangle,
  Layers,
  Lock,
  Eye,
  Sparkles,
  Calendar,
  Award,
  Compass,
  Download,
  BookOpen,
  ExternalLink,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  UserCheck,
  Scale,
  ShoppingCart,
  Target,
  Home,
  Banknote,
  Scissors,
} from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";
import LeadModal from "../../components/public/LeadModal";
import { IconVisual } from "./MarketingHome";

export default function ForInvestors() {
  const [modalOpen, setModalOpen] = useState(false);
  const openSchedule = () => setModalOpen(true);
  return (
    <PublicLayout title="Private Investors">
      <Hero onSchedule={openSchedule} />
      <ComfortStrip />
      <PortfolioFeatures />
      <BehindTheScenes />
      <ClarityYouSee />
      <PrivacyAndTrust />
      <StartingWithUs />
      <KnowledgeCentre />
      <RegulatoryDisclosure />
      <EscalationMatrix />
      <ContactInfo />
      <ConversationStarter onSchedule={openSchedule} />
      {modalOpen && (
        <LeadModal
          onClose={() => setModalOpen(false)}
          defaultType="investor"
          source="for_investors_page"
          title="Tell us about you."
          subtitle="We'll connect you with a Kinntegra-powered MFD that fits your goals. No obligation, no spam."
        />
      )}
    </PublicLayout>
  );
}

/* ─────────────────── HERO ─────────────────── */
function Hero({ onSchedule }) {
  return (
    <section data-testid="invest-hero" className="relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-[#C8A96B]/10 blur-[140px]" />
        <div className="absolute top-1/2 -left-32 h-[500px] w-[500px] rounded-full bg-[#1a3f3a]/30 blur-[160px]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-10 md:pt-16 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs tracking-widest uppercase text-white/70 mb-8">
              <Sparkles size={12} className="text-[#C8A96B]" />
              For Private Investors
            </div>

            <h1
              data-testid="invest-headline"
              className="font-light text-[44px] leading-[1.05] sm:text-[60px] lg:text-[74px] tracking-[-0.025em]"
            >
              Your portfolio,
              <br />
              <span className="font-normal italic text-[#C8A96B]">
                seen with clarity.
              </span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed font-light">
              You hired a mutual-fund distributor (MFD) because you trust their
              judgement. Kinntegra is the platform underneath their
              practice — the layer that lets them see your investments end-to-end
              and give you a portfolio view that's accurate, reconciled, and
              always current.
            </p>

            <p className="mt-5 text-base text-white/55 max-w-2xl leading-relaxed">
              This page walks you through everything Kinntegra surfaces in
              your Portfolio tab — and the tools your MFD uses behind the
              scenes to manage your money with more precision.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={onSchedule}
                data-testid="invest-cta-schedule"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition active:scale-[0.98]"
              >
                Book a Consultation
                <ArrowUpRight size={16} />
              </button>
              <Link
                to="/login"
                data-testid="invest-cta-login"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white/85 hover:bg-white/[0.04] transition"
              >
                Sign In to Your Portfolio
              </Link>
            </div>
          </div>

          {/* Portfolio mock */}
          <div className="lg:col-span-5">
            <PortfolioMock />
          </div>
        </div>
      </div>
    </section>
  );
}

function PortfolioMock() {
  return (
    <div className="relative" data-testid="invest-hero-mock">
      <div className="absolute -inset-6 bg-gradient-to-br from-[#C8A96B]/20 to-transparent rounded-3xl blur-2xl" />
      <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-sm p-6 shadow-2xl">
        <div className="text-[10px] tracking-widest uppercase text-white/40">
          Your Portfolio
        </div>
        <div className="mt-2 text-4xl font-light text-white">₹ 2.84 Cr</div>
        <div className="mt-1 flex items-center gap-2 text-xs">
          <span className="text-emerald-300 flex items-center gap-1">
            <TrendingUp size={11} /> +₹ 38.6 L
          </span>
          <span className="text-white/40">since you started</span>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Stat label="XIRR" value="14.2%" tone="gold" />
          <Stat label="Schemes" value="18" />
          <Stat label="Folios" value="11" />
          <Stat label="Holding (avg)" value="6.3 yr" />
        </div>

        <div className="mt-5 pt-5 border-t border-white/5 space-y-2.5">
          {[
            { name: "Equity Funds", pct: 62 },
            { name: "Hybrid Funds", pct: 22 },
            { name: "Debt Funds", pct: 16 },
          ].map((r) => (
            <div key={r.name} className="flex items-center gap-3">
              <span className="text-xs text-white/55 w-28">{r.name}</span>
              <span className="flex-1 h-[3px] bg-white/5 rounded-full overflow-hidden">
                <span
                  className="block h-full bg-gradient-to-r from-[#C8A96B] to-[#C8A96B]/40"
                  style={{ width: `${r.pct}%` }}
                />
              </span>
              <span className="text-xs text-white/90 tabular-nums">{r.pct}%</span>
            </div>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between text-[11px] text-white/40">
          <span>Reconciled with CAMS + KARVY</span>
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
      <div className="text-[10px] tracking-widest uppercase text-white/40">
        {label}
      </div>
      <div
        className={`mt-1 text-xl font-light ${
          tone === "gold" ? "text-[#C8A96B]" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

/* ─────────────────── COMFORT STRIP ─────────────────── */
function ComfortStrip() {
  const items = [
    { icon: ShieldCheck, label: "Reconciled with CAMS & KARVY" },
    { icon: Lock, label: "View-only, secure access" },
    { icon: Eye, label: "Audit-grade history" },
    { icon: Award, label: "Built for long-term holdings" },
  ];
  return (
    <section className="border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((i) => (
          <div
            key={i.label}
            className="flex items-center gap-3 text-sm text-white/65"
          >
            <i.icon size={16} className="text-[#C8A96B] shrink-0" />
            <span className="leading-snug">{i.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────── PORTFOLIO FEATURES ─────────────────── */
function PortfolioFeatures() {
  const features = [
    {
      icon: BarChart3,
      tab: "Summary",
      title: "A single view of everything you own",
      copy: "Total invested, current market value, gains, XIRR — across every folio, every AMC, every joint and minor holding. Already reconciled with the registrars. No spreadsheets needed.",
    },
    {
      icon: PieChart,
      tab: "Scheme-Wise Breakdown",
      title: "Where your money actually sits",
      copy: "See how your portfolio distributes across schemes, asset categories, and AMCs. Spot concentration risk before it matters.",
    },
    {
      icon: Users,
      tab: "Sub-Accounts",
      title: "Family-wise visibility",
      copy: "Self, joint, minor (as guardian), as a joint second holder — every holding is grouped by who actually owns it. Switch between views with one click.",
    },
    {
      icon: TrendingUp,
      tab: "Active Units",
      title: "What's still working for you",
      copy: "A FIFO-tracked view of the units you still hold, when you bought them, at what cost — so XIRR and unrealised gains reflect reality, not assumptions.",
    },
    {
      icon: TrendingUp,
      tab: "Sold Units",
      title: "What you've already realised",
      copy: "Every redemption mapped back to its original purchase lot. The full story of every exit decision.",
    },
    {
      icon: DollarSign,
      tab: "Dividend Payout",
      title: "Income, tracked properly",
      copy: "Every IDCW payout dated, amounted, and reconciled — separately from reinvestment plans, so taxable income is never confused with NAV-reinvested units.",
    },
    {
      icon: RefreshCcw,
      tab: "All Transactions",
      title: "Every transaction. Searchable.",
      copy: "Years of buys, sells, SIPs, switches, dividends — filterable by date range, financial year, scheme, AMC. Export anytime as Excel.",
    },
    {
      icon: Scissors,
      tab: "Provisional Sell",
      title: "Tax-aware exits, simulated before you commit",
      copy: "Plan a redemption across folios and let the platform show LTCG / STCG impact lot-by-lot — before a single rupee moves. Pair it with the LTCG ₹1.25 L annual harvesting window and capture profits without surprises in March.",
    },
    {
      icon: ShoppingCart,
      tab: "Provisional Buy",
      title: "Allocation planning, lined up to your strategy",
      copy: "Tell us how much you want to deploy. We map it across your Wealth / Thematic / Foreign sleeves at AMC-weighted percentages — Liquid auto-populated per AMC — and let you commit only when the math checks out.",
    },
    {
      icon: Target,
      tab: "Profit Realignment",
      title: "Lock gains. Reset the timer.",
      copy: "Identify where unrealised LTCG can be booked tax-free, then redeploy into the same or a different scheme — resetting the holding-period clock while keeping your market exposure intact. The platform shows you exactly which lots, which schemes, which amounts.",
    },
    {
      icon: Award,
      tab: "Exit-Free Lots",
      title: "Know which units you can sell without paying tax",
      copy: "Every active lot grouped by scheme / folio / bucket, with expandable rows showing when it crossed the 12-month LTCG threshold and how much headroom remains in your ₹1.25 L tax-free window this FY.",
    },
    {
      icon: Home,
      tab: "Family View",
      title: "Everyone you care about. One screen.",
      copy: "Self, spouse, parents, children, joint holdings, minor holdings — grouped under one family unit. See combined AUM, combined XIRR, combined goal-progress. Switch back to your own view in one click.",
    },
    {
      icon: AlertTriangle,
      tab: "Unit Mismatch",
      title: "We catch what others miss",
      copy: "If our active-units number ever drifts from the registrar's, the platform flags it for your MFD to review. You never silently lose units to a mis-classified transaction.",
    },
    {
      icon: FileText,
      tab: "Folio Details",
      title: "Every folio. Every detail.",
      copy: "Bank account, nominee, contact, communication mode — for every folio you own, refreshed nightly from the registrars.",
    },
    {
      icon: Bell,
      tab: "Reporting Preferences",
      title: "Reports your way",
      copy: "Monthly, quarterly, or on-demand. Email, WhatsApp, or both. Your MFD configures it once — it just keeps coming.",
    },
    {
      icon: AlertTriangle,
      tab: "Segregated Portfolios",
      title: "We don't hide the side-pockets",
      copy: "If any of your schemes have side-pocketed (segregated) units, you see them — with current status, NAV, and write-off history. Full transparency.",
    },
    {
      icon: Layers,
      tab: "Failed Transactions",
      title: "Nothing slips through",
      copy: "SIP rejections, OTM bounces, dispatch failures — every event captured so you and your MFD can fix it before it compounds.",
    },
  ];

  return (
    <section
      data-testid="invest-portfolio-features"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>What you'll see in your Portfolio tab</SectionEyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] max-w-3xl leading-[1.12]">
          Seventeen views.{" "}
          <span className="text-white/40">One coherent story.</span>
        </h2>
        <p className="mt-6 text-lg text-white/55 max-w-2xl font-light leading-relaxed">
          Every tab below is a real surface inside your Portfolio screen.
          No buzzwords. No filler.
        </p>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {features.map((f) => (
            <FeatureCard key={f.tab} {...f} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, tab, title, copy }) {
  return (
    <div
      className="group bg-[#07111F] p-8 hover:bg-[#0c1827] transition relative overflow-hidden"
      data-testid={`invest-feature-${tab.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#C8A96B]/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
      <div className="flex items-center gap-3 mb-5">
        <Icon size={18} className="text-[#C8A96B]" strokeWidth={1.4} />
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          Tab · {tab}
        </span>
      </div>
      <h3 className="text-lg font-light text-white mb-3 tracking-tight leading-snug">
        {title}
      </h3>
      <p className="text-sm text-white/55 leading-relaxed">{copy}</p>
    </div>
  );
}

/* ─────────────────── BEHIND THE SCENES (MFD TOOLS) ─────────────────── */
function BehindTheScenes() {
  const tools = [
    {
      icon: RefreshCcw,
      title: "Daily multi-registrar reconciliation",
      copy: "Your MFD doesn't manually chase CAMS and KARVY files. Kinntegra pulls them automatically every day and matches every transaction against your folio — so the numbers your MFD shares are always live, never stale.",
    },
    {
      icon: AlertTriangle,
      title: "Exception logs they actually act on",
      copy: "SIP failures, NAV mismatches, transfer-out events — the platform flags them the moment they happen. Your MFD calls you before you'd ever have noticed.",
    },
    {
      icon: PieChart,
      title: "Family-grouped intelligence",
      copy: "Spouse, children, HUF, joint holdings — your MFD sees the entire household's allocation, not just one PAN. They can spot over-concentration and rebalance with the whole picture.",
    },
    {
      icon: TrendingUp,
      title: "Holding-period XIRR by tranche",
      copy: "Most MFDs eyeball returns. Yours computes XIRR cohort-by-cohort: 5+ years, 3-5, 1-3, &lt;1 — and looks for under-performers before you have to ask.",
    },
    {
      icon: Compass,
      title: "Continuity & succession planning",
      copy: "Every nominee, every bank, every contact — versioned and audit-logged. If something happens to you (or your MFD), nothing about your portfolio is in someone's head.",
    },
    {
      icon: Calendar,
      title: "Cashflow forecasting",
      copy: "Upcoming SIPs, STPs, SWPs — your MFD has a clear forward view of money in / out. Useful for tax planning, liquidity events, and reviews.",
    },
  ];

  return (
    <section
      data-testid="invest-mfd-tools"
      className="py-16 md:py-20 border-b border-white/5 relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[800px] rounded-full bg-[#1a3f3a]/20 blur-[140px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>What your MFD has, behind the scenes</SectionEyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] max-w-4xl leading-[1.12]">
          Your MFD has tools that look more like{" "}
          <span className="italic text-[#C8A96B]">
            an institutional desk
          </span>{" "}
          than a spreadsheet.
        </h2>

        <div className="mt-12 grid md:grid-cols-2 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {tools.map((t) => (
            <div
              key={t.title}
              data-testid={`invest-mfd-tool-${t.title.slice(0, 20).toLowerCase().replace(/\s/g, "-")}`}
              className="bg-[#07111F] p-8 md:p-10 hover:bg-[#0c1827] transition"
            >
              <t.icon
                size={22}
                className="text-[#C8A96B] mb-7"
                strokeWidth={1.4}
              />
              <h3 className="text-xl font-light text-white tracking-tight">
                {t.title}
              </h3>
              <p
                className="mt-4 text-sm text-white/55 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t.copy }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── CLARITY YOU SEE ─────────────────── */
function ClarityYouSee() {
  const points = [
    {
      n: "01",
      title: "Numbers that match the registrar",
      copy: "Every value on your Portfolio is reconciled against the official KFintech and CAMS records overnight. If your MFD says ₹2.84 Cr, the registrar's books agree.",
    },
    {
      n: "02",
      title: "Reinvestment vs. Payout, never confused",
      copy: "IDCW Reinvestment buys you more units. IDCW Payout puts cash in your bank. The platform tracks the two separately so your taxable income and growth aren't muddled.",
    },
    {
      n: "03",
      title: "Joint holdings, minor folios, HUF — all in one place",
      copy: "Even when you're the second holder or the guardian, the holding shows up. Nothing about your household is invisible.",
    },
    {
      n: "04",
      title: "Goals you set with your MFD, tracked silently",
      copy: "Retirement, education, a home — when you and your MFD set a goal, the platform watches the progress without you needing to log in.",
    },
  ];

  return (
    <section
      data-testid="invest-clarity"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>What clarity feels like</SectionEyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] max-w-3xl leading-[1.12]">
          You stop wondering if the numbers are right.
        </h2>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-0 lg:gap-12 border-t border-white/5 lg:border-0">
          {points.map((p, i) => (
            <div
              key={p.n}
              data-testid={`invest-clarity-${p.n}`}
              className={`py-8 lg:py-0 ${
                i !== points.length - 1
                  ? "border-b lg:border-b-0 border-white/5"
                  : ""
              }`}
            >
              <div className="text-[42px] font-extralight text-[#C8A96B]/40 tabular-nums">
                {p.n}
              </div>
              <h3 className="mt-4 text-lg font-light text-white tracking-tight leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-white/55 leading-relaxed max-w-xs">
                {p.copy}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── PRIVACY ─────────────────── */
function PrivacyAndTrust() {
  return (
    <section
      data-testid="invest-privacy"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5">
          <IconVisual
            items={[
              { icon: Lock, label: "PAN + PIN" },
              { icon: Eye, label: "View-only" },
              { icon: ShieldCheck, label: "Encrypted" },
              { icon: FileText, label: "Audit trail" },
              { icon: AlertTriangle, label: "Change alerts" },
              { icon: Users, label: "Your data" },
            ]}
            eyebrow="View-only by design"
            caption="PAN-based login + 4-digit PIN. No transactions, no aggregator behaviour, no data resale."
            testId="invest-privacy-image"
            ratio="aspect-[5/6]"
          />
        </div>
        <div className="md:col-span-7">
          <SectionEyebrow>Privacy & access</SectionEyebrow>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.15]">
            Your data stays{" "}
            <span className="italic text-[#C8A96B]">yours.</span>
          </h2>
          <div className="mt-8 space-y-6 text-white/65 text-base leading-[1.75] font-light max-w-2xl">
            <p>
              Kinntegra is infrastructure for your MFD's practice — not a
              broker, not an aggregator, not a marketplace. We don't trade on
              your behalf, we don't market funds to you, and we don't sell
              your data to anyone.
            </p>
            <p>
              Your access is{" "}
              <span className="text-white">view-only</span> and protected by a
              PAN-based login plus a 4-digit security PIN. Your MFD cannot
              transact on your behalf through Kinntegra — they continue to use
              the standard registrar / exchange channels for that.
            </p>
            <p>
              Every change to your record (bank update, nominee change,
              contact mode) leaves an audit-grade trail visible to you and to
              your MFD. Nothing happens silently.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── STARTING WITH US — DOWNLOADABLE PROCESSES ─────────────────── */
function StartingWithUs() {
  const items = [
    {
      icon: Compass,
      title: "Fund Selection Process",
      copy:
        "How a Kinntegra-powered MFD evaluates schemes against your risk profile, goals, and time horizon.",
      href: "/docs/fund-selection-process.pdf",
      testId: "invest-download-fsp",
    },
    {
      icon: FileText,
      title: "Letter of Engagement",
      copy:
        "The transparent client–distributor agreement that governs how your account is managed and serviced.",
      href: "/docs/letter-of-engagement.pdf",
      testId: "invest-download-loe",
    },
    {
      icon: UserCheck,
      title: "Account Opening Process",
      copy:
        "Step-by-step KYC, documentation, and onboarding checklist — designed for clarity from day one.",
      href: "/docs/account-opening-process.pdf",
      testId: "invest-download-aop",
    },
  ];
  return (
    <section
      data-testid="invest-starting"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>Starting with us</SectionEyebrow>
        <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.15] max-w-3xl">
          Things to <span className="italic text-[#C8A96B]">know</span> before
          you begin.
        </h2>
        <p className="mt-6 text-base text-white/60 font-light leading-relaxed max-w-2xl">
          Three short documents that explain exactly how your relationship,
          paperwork, and portfolio management work. Read them before you
          decide — not after.
        </p>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {items.map((it) => (
            <a
              key={it.title}
              href={it.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={it.testId}
              className="group relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c1827] via-[#0a1422] to-[#07111F] p-7 hover:border-[#C8A96B]/40 transition-colors"
            >
              <span className="absolute top-5 left-5 h-3 w-3 border-t border-l border-[#C8A96B]/40" />
              <span className="absolute top-5 right-5 h-3 w-3 border-t border-r border-[#C8A96B]/40" />
              <div className="flex items-start justify-between mb-6">
                <div className="w-11 h-11 rounded-lg bg-[#C8A96B]/10 border border-[#C8A96B]/30 flex items-center justify-center">
                  <it.icon size={20} strokeWidth={1.4} className="text-[#C8A96B]" />
                </div>
                <Download
                  size={16}
                  className="text-white/40 group-hover:text-[#C8A96B] transition"
                />
              </div>
              <h3 className="text-lg font-light text-white tracking-tight mb-2">
                {it.title}
              </h3>
              <p className="text-sm text-white/55 leading-relaxed">
                {it.copy}
              </p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#C8A96B] group-hover:gap-2.5 transition-all">
                Download PDF
                <ArrowUpRight size={12} />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── KNOWLEDGE CENTRE ─────────────────── */
function KnowledgeCentre() {
  return (
    <section
      data-testid="invest-knowledge"
      className="py-14 md:py-16 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-center rounded-2xl border border-white/10 bg-white/[0.02] p-8 md:p-10">
          <div className="md:col-span-2 flex md:justify-center">
            <div className="w-14 h-14 rounded-xl bg-[#C8A96B]/10 border border-[#C8A96B]/30 flex items-center justify-center">
              <BookOpen size={24} strokeWidth={1.3} className="text-[#C8A96B]" />
            </div>
          </div>
          <div className="md:col-span-7">
            <SectionEyebrow>Knowledge Centre</SectionEyebrow>
            <h3 className="mt-3 text-xl md:text-2xl font-light text-white tracking-tight">
              All about mutual funds — straight from AMFI.
            </h3>
            <p className="mt-3 text-sm md:text-base text-white/55 font-light leading-relaxed">
              Independent investor education from the Association of Mutual
              Funds in India. Concepts, calculators, schemes — no marketing,
              no spin.
            </p>
          </div>
          <div className="md:col-span-3 md:text-right">
            <a
              href="https://www.amfiindia.com/investor#online-centre"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="invest-knowledge-link"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#C8A96B]/50 text-[#C8A96B] hover:bg-[#C8A96B]/10 transition text-sm"
            >
              Visit AMFI
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── REGULATORY / COMMISSIONS DISCLOSURE ─────────────────── */
function RegulatoryDisclosure() {
  return (
    <section
      data-testid="invest-regulatory"
      className="py-14 md:py-16 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <SectionEyebrow>SEBI disclosure</SectionEyebrow>
            <h2 className="mt-4 text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.15]">
              Commissions on{" "}
              <span className="italic text-[#C8A96B]">mutual funds</span>.
            </h2>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-2xl border border-white/10 bg-[#0c1827]/60 p-7 relative">
              <span className="absolute top-5 left-5 h-3 w-3 border-t border-l border-[#C8A96B]/40" />
              <span className="absolute top-5 right-5 h-3 w-3 border-t border-r border-[#C8A96B]/40" />
              <div className="flex items-start gap-3 mb-5">
                <Scale size={18} className="text-[#C8A96B] mt-1 shrink-0" />
                <p className="text-sm text-white/65 leading-relaxed">
                  As per SEBI circular{" "}
                  <span className="font-mono text-white/85 text-[13px]">
                    SEBI/IMD/CIR No. 4/168230/09
                  </span>
                  , the comparative commissions earned by Kinntegra (AMFI
                  Registered Mutual Funds Distributor) from each fund-house
                  whose products are distributed are published in full.
                </p>
              </div>
              <a
                href="/commissions-on-mutual-funds"
                data-testid="invest-commissions-link"
                className="inline-flex items-center gap-2 text-sm text-[#C8A96B] hover:underline"
              >
                View commissions on mutual funds
                <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── ESCALATION MATRIX ─────────────────── */
function EscalationMatrix() {
  const rows = [
    {
      role: "Customer Care",
      icon: Users,
      name: "Nilesh Jagtap",
      email: "accounts@kinntegra.co.in",
      phone: "+91 96196 46754",
      hours: "9 AM – 6 PM",
      testId: "escalation-customer-care",
    },
    {
      role: "Director — Operations",
      icon: Briefcase,
      name: "Krutika Bisani",
      email: "info@kinntegra.co.in",
      phone: "+91 98252 13510",
      hours: "9 AM – 6 PM",
      testId: "escalation-director-ops",
    },
    {
      role: "Managing Director",
      icon: Award,
      name: "Punit Bisani",
      email: "punit@kinntegra.co.in",
      phone: "+91 91360 51689",
      hours: "9 AM – 6 PM",
      testId: "escalation-md",
    },
  ];
  return (
    <section
      data-testid="invest-escalation"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>Escalation matrix</SectionEyebrow>
        <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.15] max-w-3xl">
          A clear escalation path —{" "}
          <span className="italic text-[#C8A96B]">all three levels.</span>
        </h2>
        <p className="mt-6 text-base text-white/55 font-light leading-relaxed max-w-2xl">
          Investor grievances rarely arise — but when they do, you should
          know exactly who to reach, in what order, and within which hours.
        </p>

        {/* Desktop table */}
        <div className="mt-12 hidden md:block">
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-12 bg-white/5 text-[11px] uppercase tracking-[0.18em] text-white/45 px-6 py-4">
              <div className="col-span-3">Level</div>
              <div className="col-span-3">Contact</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Phone</div>
              <div className="col-span-1 text-right">Hours</div>
            </div>
            {rows.map((r, idx) => (
              <div
                key={r.role}
                data-testid={r.testId}
                className={`grid grid-cols-12 items-center px-6 py-5 text-sm ${idx > 0 ? "border-t border-white/5" : ""} hover:bg-white/[0.03] transition`}
              >
                <div className="col-span-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#C8A96B]/10 border border-[#C8A96B]/30 flex items-center justify-center shrink-0">
                    <r.icon size={16} strokeWidth={1.4} className="text-[#C8A96B]" />
                  </div>
                  <span className="text-white/80 font-light">{r.role}</span>
                </div>
                <div className="col-span-3 text-white font-light">{r.name}</div>
                <div className="col-span-3">
                  <a
                    href={`mailto:${r.email}`}
                    className="text-[#C8A96B]/85 hover:text-[#C8A96B] transition text-[13px] break-all"
                  >
                    {r.email}
                  </a>
                </div>
                <div className="col-span-2">
                  <a
                    href={`tel:${r.phone.replace(/\s/g, "")}`}
                    className="text-white/70 hover:text-white transition font-mono text-[13px]"
                  >
                    {r.phone}
                  </a>
                </div>
                <div className="col-span-1 text-right text-white/45 text-[12px]">
                  {r.hours}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile stacked cards */}
        <div className="mt-10 md:hidden space-y-3">
          {rows.map((r) => (
            <div
              key={r.role}
              data-testid={`${r.testId}-mobile`}
              className="rounded-xl border border-white/10 bg-[#0c1827]/50 p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#C8A96B]/10 border border-[#C8A96B]/30 flex items-center justify-center shrink-0">
                  <r.icon size={18} strokeWidth={1.4} className="text-[#C8A96B]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                    {r.role}
                  </div>
                  <div className="text-base text-white font-light">{r.name}</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-[#C8A96B]/85 break-all">
                  <Mail size={13} className="shrink-0" />
                  {r.email}
                </a>
                <a href={`tel:${r.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-white/70 font-mono text-[13px]">
                  <Phone size={13} className="shrink-0" />
                  {r.phone}
                </a>
                <div className="flex items-center gap-2 text-white/40 text-[12px]">
                  <Calendar size={13} className="shrink-0" />
                  {r.hours}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── CONTACT INFO ─────────────────── */
function ContactInfo() {
  const items = [
    {
      icon: Phone,
      label: "Telephone / WhatsApp",
      value: "+91 91360 51689",
      href: "tel:+919136051689",
      testId: "contact-phone",
    },
    {
      icon: Mail,
      label: "Email",
      value: "info@kinntegra.co.in",
      href: "mailto:info@kinntegra.co.in",
      testId: "contact-email",
    },
    {
      icon: MapPin,
      label: "Office",
      value:
        "6302, Island City Center Two, G D Ambekar Marg, Dadar (East), Mumbai 400014",
      href: null,
      testId: "contact-address",
    },
  ];
  return (
    <section
      data-testid="invest-contact"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>Contacting Kinntegra</SectionEyebrow>
        <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.15] max-w-3xl">
          We're here to{" "}
          <span className="italic text-[#C8A96B]">help.</span>
        </h2>
        <p className="mt-6 text-base text-white/55 font-light leading-relaxed max-w-2xl">
          Any question — about your account, the platform, or how a
          Kinntegra-powered MFD can fit into your financial life.
        </p>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {items.map((it) => {
            const Wrap = it.href ? "a" : "div";
            const props = it.href
              ? { href: it.href, target: it.href.startsWith("mailto") || it.href.startsWith("tel") ? undefined : "_blank", rel: "noopener noreferrer" }
              : {};
            return (
              <Wrap
                key={it.label}
                {...props}
                data-testid={it.testId}
                className={`relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c1827] via-[#0a1422] to-[#07111F] p-7 ${it.href ? "hover:border-[#C8A96B]/40 transition-colors block" : ""}`}
              >
                <span className="absolute top-5 left-5 h-3 w-3 border-t border-l border-[#C8A96B]/40" />
                <span className="absolute top-5 right-5 h-3 w-3 border-t border-r border-[#C8A96B]/40" />
                <div className="w-11 h-11 rounded-lg bg-[#C8A96B]/10 border border-[#C8A96B]/30 flex items-center justify-center mb-6">
                  <it.icon size={20} strokeWidth={1.4} className="text-[#C8A96B]" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-2">
                  {it.label}
                </div>
                <div className="text-base text-white font-light leading-relaxed break-words">
                  {it.value}
                </div>
              </Wrap>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── CTA ─────────────────── */
function ConversationStarter({ onSchedule }) {
  return (
    <section
      data-testid="invest-cta"
      className="py-20 md:py-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[400px] w-[800px] rounded-full bg-[#C8A96B]/[0.06] blur-[120px]" />
      </div>
      <div className="relative max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.1]">
          A platform is only as good as
          <br />
          <span className="italic text-[#C8A96B]">the MFD behind it.</span>
        </h2>
        <p className="mt-8 text-lg text-white/60 font-light leading-relaxed max-w-2xl mx-auto">
          Talk to a Kinntegra-powered MFD. They'll understand your goals,
          map out a portfolio, and give you the institutional-grade view
          shown above — no obligation.
        </p>
        <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <button
            onClick={onSchedule}
            data-testid="invest-cta-final-schedule"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition"
          >
            Book a Consultation
            <ArrowUpRight size={16} />
          </button>
          <Link
            to="/login"
            data-testid="invest-cta-final-login"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/15 text-white/85 hover:bg-white/[0.04] transition"
          >
            Sign In to Your Portfolio
          </Link>
        </div>
        <p className="mt-10 text-xs text-white/40 max-w-xl mx-auto">
          Already have an MFD? Ask them — if they're on Kinntegra, they can
          grant you secure view-only access in seconds.
        </p>
      </div>
    </section>
  );
}


/* ─────────────────── shared ─────────────────── */
function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
      <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
      {children}
    </div>
  );
}
